import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as customersApi from '@/api/customers'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { AiAnswer } from '@/components/AiAnswer'
import { AppShell } from '@/components/AppShell'
import { SettingsShell } from '@/components/SettingsShell'
import {
  CUSTOM_MODEL,
  modelSelectValue,
  providerMeta,
  resolveModelId,
} from '@/lib/aiModels'
import { getRememberedSettingsCustomer } from '@/settingsContext'

export function AiSearch() {
  const { isAdmin, isCustomer, canUseAiSearch } = useAuth()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [customers, setCustomers] = useState([])
  const [configured, setConfigured] = useState(false)
  const [provider, setProvider] = useState(null)
  const [defaultModel, setDefaultModel] = useState(null)
  const [modelChoice, setModelChoice] = useState('')
  const [customModel, setCustomModel] = useState('')
  const [query, setQuery] = useState('')
  const [filterCustomerId, setFilterCustomerId] = useState('')
  const [loadingMeta, setLoadingMeta] = useState(true)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)

  useEffect(() => {
    if (!canUseAiSearch) return undefined
    let cancelled = false
    async function load() {
      setLoadingMeta(true)
      try {
        const [ai, cust] = await Promise.all([
          settingsApi.getAiStatus(),
          customersApi.listCustomers(),
        ])
        if (cancelled) return
        setConfigured(Boolean(ai.configured))
        setProvider(ai.provider)
        const saved = resolveModelId(
          ai.model || providerMeta(ai.provider || 'openai').defaultModel,
        )
        setDefaultModel(saved)
        setModelChoice(modelSelectValue(ai.provider || 'openai', saved))
        if (modelSelectValue(ai.provider || 'openai', saved) === CUSTOM_MODEL) {
          setCustomModel(saved)
        }
        const list = cust || []
        setCustomers(list)
        if (isCustomer && list.length === 1) {
          setFilterCustomerId(String(list[0].id))
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load AI Search.',
          )
        }
      } finally {
        if (!cancelled) setLoadingMeta(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [canUseAiSearch, isCustomer])

  if (!canUseAiSearch) {
    return (
      <AppShell title="AI Search">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">
            AI Search
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-brand-muted">
            AI Search is not enabled for your account. Ask an administrator if
            you need access.
          </p>
          <Link to="/" className="mt-4 inline-block text-sm font-medium underline">
            Back to home
          </Link>
        </div>
      </AppShell>
    )
  }

  function effectiveModel() {
    if (modelChoice === CUSTOM_MODEL) return customModel.trim()
    return modelChoice || defaultModel
  }

  async function handleSearch(e) {
    e.preventDefault()
    const q = query.trim()
    if (q.length < 2) {
      setError('Enter a question of at least 2 characters.')
      return
    }
    const model = effectiveModel()
    if (!model) {
      setError('Choose a model (or enter a custom model id).')
      return
    }
    setSearching(true)
    setError('')
    setResult(null)
    try {
      const data = await settingsApi.aiSearch({
        query: q,
        customer_id: filterCustomerId ? Number(filterCustomerId) : undefined,
        model,
      })
      setResult(data)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Search failed.',
      )
    } finally {
      setSearching(false)
    }
  }

  const meta = providerMeta(provider || 'openai')
  const showCustomerFilter = customers.length > 1
  const scopeHint = isCustomer
    ? customers.length === 1
      ? `Searches your knowledge base (${customers[0]?.name || 'your company'}).`
      : 'Searches only the customers you can access.'
    : 'Ask questions across customer knowledge bases.'

  const body = (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
        <h1 className="font-display text-4xl tracking-tight text-brand">
          AI Search
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-brand-muted">
          {scopeHint} Matches pages, tables, notes and attachment names, then
          answers with the site AI. Password fields are redacted before anything
          is sent to the provider.
        </p>
        {!loadingMeta && (
          <p className="mt-3 text-xs text-brand-muted">
            {configured ? (
              <>
                Provider <strong>{provider}</strong>. Default model{' '}
                <strong>{defaultModel}</strong>.
                {isAdmin ? (
                  <>
                    {' '}
                    <Link to="/settings/ai" className="underline">
                      Change AI settings
                    </Link>
                  </>
                ) : null}
              </>
            ) : isAdmin ? (
              <>
                Not configured —{' '}
                <Link to="/settings/ai" className="underline">
                  set provider and API key
                </Link>{' '}
                first.
              </>
            ) : (
              <>AI Search is not configured yet. Ask an administrator to enable it.</>
            )}
          </p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <form
        onSubmit={handleSearch}
        className="space-y-3 rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm"
      >
        <label className="block text-sm">
          <span className="text-brand-muted">Question</span>
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
            placeholder={
              isCustomer
                ? 'e.g. What is the VPN address? When does the SSL certificate expire?'
                : 'e.g. Which customers use Mimecast? Who is the IT manager at Caseman?'
            }
            disabled={!configured || searching}
          />
        </label>
        <div
          className={[
            'grid gap-3',
            showCustomerFilter ? 'sm:grid-cols-2' : 'sm:grid-cols-1 sm:max-w-md',
          ].join(' ')}
        >
          <label className="block text-sm">
            <span className="text-brand-muted">Model for this search</span>
            <select
              value={modelChoice}
              onChange={(e) => {
                const choice = e.target.value
                setModelChoice(choice)
                if (choice !== CUSTOM_MODEL) setCustomModel('')
              }}
              className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
              disabled={!configured || searching}
            >
              {meta.models.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
              {isAdmin ? (
                <option value={CUSTOM_MODEL}>Custom model id…</option>
              ) : null}
            </select>
          </label>
          {showCustomerFilter && (
            <label className="block text-sm">
              <span className="text-brand-muted">
                {isCustomer ? 'Customer' : 'Limit to customer (optional)'}
              </span>
              <select
                value={filterCustomerId}
                onChange={(e) => setFilterCustomerId(e.target.value)}
                className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
                disabled={!configured || searching}
              >
                <option value="">
                  {isCustomer ? 'All my customers' : 'All customers'}
                </option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {isAdmin && modelChoice === CUSTOM_MODEL && (
          <label className="block max-w-md text-sm">
            <span className="text-brand-muted">Custom model id</span>
            <input
              value={customModel}
              onChange={(e) => setCustomModel(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
              placeholder={meta.defaultModel}
              disabled={!configured || searching}
              required
            />
          </label>
        )}
        <button
          type="submit"
          disabled={!configured || searching || query.trim().length < 2}
          className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {result && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-brand">Answer</h2>
            <p className="mt-1 text-xs text-brand-muted">
              {result.provider} / {result.model}
            </p>
            <AiAnswer answer={result.answer} />
          </div>
          {result.sources?.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-brand">Sources</h2>
              <ul className="mt-3 space-y-3">
                {result.sources.map((src, idx) => (
                  <li
                    key={`${src.kind}-${src.customer_id}-${idx}`}
                    className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium text-brand">{src.label}</span>
                      <span className="text-xs uppercase tracking-wide text-brand-muted">
                        {src.kind}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-brand-muted line-clamp-3">
                      {src.snippet}
                    </p>
                    {src.href && (
                      <Link
                        to={src.href}
                        className="mt-1 inline-block text-xs font-medium underline"
                      >
                        Open page
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )

  if (isAdmin) {
    return (
      <SettingsShell title="AI Search" customerId={customerId || undefined}>
        {body}
      </SettingsShell>
    )
  }

  return <AppShell title="AI Search">{body}</AppShell>
}
